/**
 * Sonner (Toast) Component
 * Based on shadcn/ui + Figma design specification
 *
 * @description shadcn/ui 기반 토스트 알림 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 */

import { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  action?: ReactNode;
  duration?: number;
}

export interface ToasterProps {
  position?: ToastPosition;
  className?: string;
}

export interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (options: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((options: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2, 9);
    setToasts((prev) => [...prev, { ...options, id }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss, dismissAll }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}

const positionStyles: Record<ToastPosition, string> = {
  'top-left': 'top-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
};

const typeStyles: Record<ToastType, { bg: string; icon: React.ReactNode }> = {
  default: { bg: 'bg-card', icon: null },
  success: { bg: 'bg-card', icon: <CheckCircle className="w-5 h-5 text-safe" /> },
  error: { bg: 'bg-card', icon: <AlertCircle className="w-5 h-5 text-danger" /> },
  warning: { bg: 'bg-card', icon: <AlertTriangle className="w-5 h-5 text-warning" /> },
  info: { bg: 'bg-card', icon: <Info className="w-5 h-5 text-primary" /> },
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        onDismiss(toast.id);
      }, toast.duration || 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, onDismiss]);

  const { bg, icon } = typeStyles[toast.type];

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border border-accent-2 p-4 shadow-lg',
        'animate-in slide-in-from-right fade-in-0',
        bg
      )}
    >
      {icon}
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{toast.title}</p>
        {toast.description && (
          <p className="text-sm text-muted-foreground mt-1">{toast.description}</p>
        )}
        {toast.action && (
          <div className="mt-2">{toast.action}</div>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function Toaster({ position = 'bottom-right', className }: ToasterProps) {
  const { toasts, dismiss } = useToast();

  return (
    <div
      className={cn(
        'fixed z-50 flex flex-col gap-2 w-[356px]',
        positionStyles[position],
        className
      )}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
      ))}
    </div>
  );
}

// Helper functions for convenience
export const toast = {
  success: (title: string, description?: string) => ({ type: 'success' as ToastType, title, description }),
  error: (title: string, description?: string) => ({ type: 'error' as ToastType, title, description }),
  warning: (title: string, description?: string) => ({ type: 'warning' as ToastType, title, description }),
  info: (title: string, description?: string) => ({ type: 'info' as ToastType, title, description }),
};
